// src/employee/pages/EmployeeCalendar.js
import React, { useEffect, useMemo, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { useEmployeeAuth } from "../auth/EmployeeAuthContext";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { FaCalendarAlt, FaFire } from "react-icons/fa";
import "./EmployeeCalendar.css";  // ← YE LINE ADD KARO

import { useNavigate } from "react-router-dom";

const localizer = momentLocalizer(moment);

export default function EmployeeCalendar() {
  const { user } = useEmployeeAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    if (!user?.email) return;

    const unsubs = [];

    // Holidays
    const holidaysQuery = query(collection(db, "holidays"));
    const unsubHolidays = onSnapshot(holidaysQuery, (snap) => {
      const data = snap.docs.map((d) => ({
        id: `h-${d.id}`,
        title: d.data().title,
        start: new Date(d.data().date),
        end: new Date(d.data().date),
        type: "holiday",
      }));
      setEvents((prev) => [...prev.filter(e => !e.id.startsWith("h-")), ...data]);
    });
    unsubs.push(unsubHolidays);

    // Tasks
    const taskQuery = query(
      collection(db, "tasks"),
      where("employeeEmails", "array-contains", user.email)
    );
    const unsubTasks = onSnapshot(taskQuery, (snap) => {
      const data = snap.docs
        .filter(d => d.data().dueDate)
        .map(d => ({
          id: `t-${d.id}`,
          title: `Deadline: ${d.data().taskTitle}`,
          start: new Date(d.data().dueDate),
          end: new Date(d.data().dueDate),
          type: "task",
          status: d.data().status,
        }));
      setEvents((prev) => [...prev.filter(e => !e.id.startsWith("t-")), ...data]);
    });
    unsubs.push(unsubTasks);

    // Leaves
    const leavesQuery = query(
      collection(db, "leaves"),
      where("employeeEmail", "==", user.email),
      where("status", "==", "approved")
    );
    const unsubLeaves = onSnapshot(leavesQuery, (snap) => {
      const data = snap.docs.map(d => ({
        id: `l-${d.id}`,
        title: `On Leave`,
        start: new Date(d.data().fromDate),
        end: new Date(d.data().toDate),
        type: "leave",
        reason: d.data().reason,
      }));
      setEvents((prev) => [...prev.filter(e => !e.id.startsWith("l-")), ...data]);
    });
    unsubs.push(unsubLeaves);

    // Daily Diaries
    const diariesQuery = query(
      collection(db, "dailyDiaries"),
      where("employeeEmail", "==", user.email)
    );
    const unsubDiaries = onSnapshot(diariesQuery, (snap) => {
      const data = snap.docs.map(d => ({
        id: `d-${d.id}`,
        title: `Diary: ${d.data().notes?.slice(0, 20)}...`,
        start: new Date(d.data().date),
        end: new Date(d.data().date),
        type: "diary",
        date: d.data().date,
      }));
      setEvents((prev) => [...prev.filter(e => !e.id.startsWith("d-")), ...data]);
    });
    unsubs.push(unsubDiaries);

    return () => unsubs.forEach(u => u());
  }, [user?.email]);

  const eventStyleGetter = (event) => {
    const colors = {
      holiday: "from-emerald-400 to-teal-500",
      task: event.status === "completed" ? "from-blue-400 to-indigo-500" : "from-cyan-400 to-blue-500",
      leave: "from-orange-400 to-amber-500",
      diary: "from-purple-400 to-pink-500"
    };
    return {
      style: {
        background: `linear-gradient(to right, ${colors[event.type].split(' ')[1]}, ${colors[event.type].split(' ')[3]})`,
        borderRadius: "8px",
        opacity: 0.95,
        color: "white",
        border: "none",
        fontWeight: "700",
        fontSize: "0.75rem",
        padding: "4px 8px",
      }
    };
  };

  const summary = useMemo(() => {
    return {
      holidays: events.filter(e => e.type === "holiday").length,
      tasks: events.filter(e => e.type === "task").length,
      leaves: events.filter(e => e.type === "leave").length,
      diaries: events.filter(e => e.type === "diary").length,
    };
  }, [events]);

  const handleSelectSlot = ({ start }) => {
    const dateStr = moment(start).format("yyyy-MM-dd");
    navigate(`/employee/dashboard/tasks?date=${dateStr}`);
  };

  const handleGoToDiary = () => {
    if (!selectedEvent) return;
    const dateStr = selectedEvent.type === "diary"
      ? selectedEvent.date
      : moment(selectedEvent.start).format("yyyy-MM-dd");
    navigate(`/employee/dashboard/tasks?date=${dateStr}`);
    setSelectedEvent(null);
  };

  return (
    <div className="min-h-screen overflow-hidden bg-neutral-950 text-white">

      {/* Hero */}
      <div className="px-10 pt-10 pb-6 flex justify-between items-end">
        <div>
          <motion.h1
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-4xl font-black bg-gradient-to-r from-white to-white/40 bg-clip-text text-transparent"
          >
            Performance Calendar
          </motion.h1>
          <p className="text-white/40 text-sm mt-1">Track your work, leaves, and milestones in one view.</p>
        </div>
        <div className="flex gap-4">
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-2xl border border-white/10">
            <div className="w-2 h-2 rounded-full bg-purple-500" />
            <span className="text-xs font-bold text-white/60">Diaries: {summary.diaries}</span>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="max-w-[1600px] mx-auto px-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="backdrop-blur-3xl bg-white/5 border border-white/10 rounded-[40px] p-8 shadow-2xl"
        >
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 600 }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={setSelectedEvent}
            onSelectSlot={handleSelectSlot}
            selectable
            views={["month", "week"]}
            popup
            className="rbc-custom-calendar"
          />
        </motion.div>
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-xl z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedEvent(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-neutral-900 border border-white/20 rounded-[40px] p-10 w-full max-w-md shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-8">
                <div>
                  <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">{selectedEvent.type}</span>
                  <h3 className="text-3xl font-black text-white leading-tight mt-1">{selectedEvent.title}</h3>
                </div>
                <button onClick={() => setSelectedEvent(null)} className="text-white/40 hover:text-white transition">
                  <FaFire size={24} />
                </button>
              </div>

              <div className="space-y-6">
                <div className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/5">
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                    <FaCalendarAlt className="text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-xs text-white/40 uppercase font-bold tracking-widest">Date</p>
                    <p className="text-white font-bold">{moment(selectedEvent.start).format("dddd, MMMM Do")}</p>
                  </div>
                </div>

                {selectedEvent.type === "leave" && (
                  <div className="bg-orange-500/10 border border-orange-500/20 p-4 rounded-2xl italic text-white/80">
                    "{selectedEvent.reason}"
                  </div>
                )}

                {selectedEvent.status && (
                  <div className="flex items-center justify-between bg-white/5 p-4 rounded-2xl border border-white/5">
                    <span className="text-sm text-white/60">Status</span>
                    <span className={`px-4 py-1 rounded-full text-[10px] font-black uppercase ${selectedEvent.status === "completed" ? "bg-emerald-500/20 text-emerald-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                      {selectedEvent.status}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-10 grid grid-cols-2 gap-4">
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="py-4 bg-white/5 hover:bg-white/10 rounded-2xl text-white/60 font-bold transition border border-white/10"
                >
                  Dismiss
                </button>
                <button
                  onClick={handleGoToDiary}
                  className="py-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:scale-105 rounded-2xl text-white font-black transition shadow-xl"
                >
                  {selectedEvent.type === "diary" ? "View Diary" : "Write Diary"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <div className="text-center mt-6 text-white/50 text-sm mb-6">
        <p>Synced with <strong className="text-cyan-300">DriveEasy</strong> — One calendar. Zero confusion.</p>
      </div>
    </div>
  );
}